import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Phone, 
  MessageSquare, 
  Users, 
  BookOpen, 
  Calendar,
  Plus,
  Search,
  Filter,
  Brain,
  Activity,
  LifeBuoy
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { SearchInput } from '../common/SearchInput';
import { WellbeingDashboard } from './WellbeingDashboard';
import { Counselling } from './Counselling';
import { SupportGroups } from './SupportGroups';
import { Resources } from './Resources';
import { WellnessPlanner } from './WellnessPlanner';
import { EmergencyContacts } from './EmergencyContacts';

interface NavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
  color: string;
}

export function WellbeingManagement() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  
  const navItems: NavItem[] = [
    { 
      id: 'counselling', 
      label: 'Counselling', 
      icon: <MessageSquare size={24} />,
      description: 'Access professional counselling services',
      color: 'bg-blue-50 text-blue-600'
    },
    { 
      id: 'support-groups', 
      label: 'Support Groups', 
      icon: <Users size={24} />,
      description: 'Connect with support groups and communities',
      color: 'bg-purple-50 text-purple-600'
    },
    { 
      id: 'resources', 
      label: 'Resources', 
      icon: <BookOpen size={24} />,
      description: 'Access mental health resources and guides',
      color: 'bg-green-50 text-green-600'
    },
    { 
      id: 'wellness-planner', 
      label: 'Wellness Planner', 
      icon: <Calendar size={24} />,
      description: 'Plan and track your wellness activities',
      color: 'bg-amber-50 text-amber-600'
    },
    { 
      id: 'emergency', 
      label: 'Emergency Contacts', 
      icon: <Phone size={24} />,
      description: 'Quick access to emergency support',
      color: 'bg-red-50 text-red-600'
    }
  ];

  const handleNavigation = (id: string) => {
    navigate(`/dashboard/wellbeing/${id}`);
  };

  useEffect(() => {
    const handler = () => {
      // Wellbeing management coming soon - no data to load yet
      console.log('Wellbeing refresh requested');
    };
    window.addEventListener('refresh-wellbeing', handler);
    return () => window.removeEventListener('refresh-wellbeing', handler);
  }, []);

  return (
    <Routes>
      <Route path="/" element={
        <div className="p-6 max-w-[1600px] mx-auto">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Wellbeing Management</h1>
              <p className="text-gray-600">Manage your mental health and wellbeing resources</p>
            </div>
            <div className="flex gap-4">
              <SearchInput
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources..."
              />
              <Button
                variant="outline"
                onClick={() => setSelectedCategory('all')}
                className={selectedCategory === 'all' ? 'bg-idecide-primary text-white' : ''}
              >
                All
              </Button>
              <Button
                variant="outline"
                onClick={() => setSelectedCategory('favorites')}
                className={selectedCategory === 'favorites' ? 'bg-idecide-primary text-white' : ''}
              >
                Favourites
              </Button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <MessageSquare className="text-blue-600" size={20} />
                <span className="font-medium text-gray-900">Counselling Sessions</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">0</p>
              <p className="text-sm text-gray-600 mt-1">Scheduled sessions</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Users className="text-purple-600" size={20} />
                <span className="font-medium text-gray-900">Support Groups</span>
              </div>
              <p className="text-2xl font-bold text-purple-600">0</p>
              <p className="text-sm text-gray-600 mt-1">Active memberships</p>
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="text-green-600" size={20} />
                <span className="font-medium text-gray-900">Wellness Activities</span>
              </div>
              <p className="text-2xl font-bold text-green-600">0</p>
              <p className="text-sm text-gray-600 mt-1">Planned activities</p>
            </div>
          </div>

          {/* Quick Actions */}
          <Card className="mb-6">
            <div className="p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/wellbeing/counselling')}
                >
                  <MessageSquare size={20} className="mr-2" />
                  Book Counselling
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/wellbeing/support-groups')}
                >
                  <Users size={20} className="mr-2" />
                  Find Support Group
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => navigate('/dashboard/wellbeing/wellness-planner')}
                >
                  <Calendar size={20} className="mr-2" />
                  Plan Activity
                </Button>
              </div>
            </div>
          </Card>

          {/* Navigation Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {navItems.map((item) => (
              <motion.div
                key={item.id}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Card
                  className="cursor-pointer h-full"
                  onClick={() => handleNavigation(item.id)}
                >
                  <div className="p-6 space-y-4">
                    <div className={`w-12 h-12 rounded-lg ${item.color} flex items-center justify-center`}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{item.label}</h3>
                      <p className="text-sm text-gray-500 mt-1">{item.description}</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      } />
      <Route path="counselling" element={<Counselling />} />
      <Route path="support-groups" element={<SupportGroups />} />
      <Route path="resources" element={<Resources />} />
      <Route path="wellness-planner" element={<WellnessPlanner />} />
      <Route path="emergency" element={<EmergencyContacts />} />
      <Route path="*" element={<Navigate to="/dashboard/wellbeing" replace />} />
    </Routes>
  );
} 